import type {
  CustomerProblem,
  CustomerProblemCreateRequest,
  CustomerProblemListQuery,
  CustomerProblemPatchRequest,
  PagedCustomerProblems,
} from '@shared/types/customer-problem';
import { api } from './client';

function buildQueryString(query: CustomerProblemListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs.length === 0 ? '' : `?${qs}`;
}

export async function listCustomerProblems(
  query: CustomerProblemListQuery = {},
): Promise<PagedCustomerProblems> {
  return api.get<PagedCustomerProblems>(`/api/customer-problems${buildQueryString(query)}`);
}

export async function getCustomerProblem(id: string): Promise<CustomerProblem> {
  return api.get<CustomerProblem>(`/api/customer-problems/${encodeURIComponent(id)}`);
}

export async function createCustomerProblem(
  body: CustomerProblemCreateRequest,
): Promise<CustomerProblem> {
  return api.post<CustomerProblem>('/api/customer-problems', body);
}

export async function patchCustomerProblem(
  id: string,
  body: CustomerProblemPatchRequest,
): Promise<CustomerProblem> {
  return api.patch<CustomerProblem>(`/api/customer-problems/${encodeURIComponent(id)}`, body);
}

export async function deleteCustomerProblem(id: string): Promise<void> {
  return api.delete(`/api/customer-problems/${encodeURIComponent(id)}`);
}

export interface InferCustomerProblemsRequest {
  target_kind: 'firm' | 'lawyer';
  target_id: string;
  dry_run?: boolean;
}

export interface InferCustomerProblemCandidate {
  problem_id: string;
  problem_name: string;
  /// 0..=100.
  auto_confidence: number;
  /// English customer_problems.stage enum value.
  stage: string;
  importance: number;
  urgency: number;
  evidence_summary: string | null;
}

export interface InferCustomerProblemResultRow {
  candidate: InferCustomerProblemCandidate;
  recommended_product_id: string | null;
  customer_problem: CustomerProblem | null;
}

export interface InferCustomerProblemsResponse {
  target_kind: 'firm' | 'lawyer';
  target_id: string;
  candidates: InferCustomerProblemResultRow[];
  upserted: number;
}

export async function inferCustomerProblems(
  body: InferCustomerProblemsRequest,
): Promise<InferCustomerProblemsResponse> {
  return api.post<InferCustomerProblemsResponse>('/api/customer-problems/infer', body);
}
